import { create } from 'zustand'

import type {
  ClockingDialogMode,
  ClockingSlot,
  ClockingTimeEntryPatch,
} from '@/features/clocking/types'
import { CLOCKING_DIALOG_MODE } from '@/features/clocking/constants'

type SlotUpdater =
  | ClockingSlot[]
  | ((current: ClockingSlot[]) => ClockingSlot[])

interface ClockingState {
  applyTimeEntryChange: (
    timeEntryId: string | number,
    patch: ClockingTimeEntryPatch
  ) => void
  applyClockingChange: (options: {
    startedSlot?: ClockingSlot | null
    stoppedSlots?: ClockingSlot[]
  }) => void
  dialogDate: string | null
  dialogMode: ClockingDialogMode
  dialogOpen: boolean
  dialogSlots: ClockingSlot[]
  openEntryDialog: (options: { date: string; slots: ClockingSlot[] }) => void
  removeSlots: (slotIds: Array<string | number>) => void
  setDialogOpen: (open: boolean) => void
  setSlots: (updater: SlotUpdater) => void
  slots: ClockingSlot[]
}

export const useClockingStore = create<ClockingState>(set => ({
  dialogDate: null,
  dialogMode: CLOCKING_DIALOG_MODE.TODAY,
  dialogOpen: false,
  dialogSlots: [],
  slots: [],
  applyTimeEntryChange: (timeEntryId, patch) =>
    set(state => {
      const applyPatch = (slots: ClockingSlot[]) =>
        slots.map(slot => {
          const slotTimeEntryId = slot.timeentry_data?.id ?? slot.timeentry
          if (String(slotTimeEntryId) !== String(timeEntryId)) return slot
          return {
            ...slot,
            is_billable: patch.is_billable ?? slot.is_billable,
            timeentry_data: {
              ...(slot.timeentry_data ?? { id: timeEntryId }),
              ...patch,
            },
          }
        })
      return {
        dialogSlots: applyPatch(state.dialogSlots),
        slots: applyPatch(state.slots),
      }
    }),
  openEntryDialog: ({ date, slots }) =>
    set({
      dialogDate: date,
      dialogMode: CLOCKING_DIALOG_MODE.ENTRY,
      dialogOpen: true,
      dialogSlots: slots,
    }),
  removeSlots: slotIds =>
    set(state => {
      const deletedIds = new Set(slotIds.map(String))
      const removeDeletedSlots = (slots: ClockingSlot[]) =>
        slots.filter(slot => !deletedIds.has(String(slot.id)))

      return {
        dialogSlots: removeDeletedSlots(state.dialogSlots),
        slots: removeDeletedSlots(state.slots),
      }
    }),
  setDialogOpen: dialogOpen =>
    set({
      dialogDate: null,
      dialogMode: CLOCKING_DIALOG_MODE.TODAY,
      dialogOpen,
      dialogSlots: [],
    }),
  setSlots: updater =>
    set(state => ({
      slots: typeof updater === 'function' ? updater(state.slots) : updater,
    })),
  applyClockingChange: ({ startedSlot = null, stoppedSlots = [] }) =>
    set(state => {
      const stoppedById = new Map(
        stoppedSlots.map(slot => [String(slot.id), slot])
      )
      const stoppedByTimeEntryId = new Map(
        stoppedSlots
          .filter(slot => slot.timeentry != null)
          .map(slot => [String(slot.timeentry), slot])
      )
      const slots = state.slots.map(slot => {
        const stoppedSlot = stoppedById.get(String(slot.id))
        const stoppedTimeEntrySlot =
          !slot.stop && slot.timeentry != null
            ? stoppedByTimeEntryId.get(String(slot.timeentry))
            : undefined
        if (stoppedSlot) {
          return { ...slot, ...stoppedSlot, id: slot.id }
        }
        if (stoppedTimeEntrySlot) {
          return { ...slot, stop: stoppedTimeEntrySlot.stop }
        }
        if (
          startedSlot &&
          !slot.stop &&
          String(slot.id) !== String(startedSlot.id)
        ) {
          return {
            ...slot,
            stop: startedSlot.start ?? new Date().toISOString(),
          }
        }
        return slot
      })

      const patchDialogSlots = (currentSlots: ClockingSlot[]) => {
        const patchedSlots = currentSlots.map(slot => {
          const stoppedSlot = stoppedById.get(String(slot.id))
          const stoppedTimeEntrySlot =
            !slot.stop && slot.timeentry != null
              ? stoppedByTimeEntryId.get(String(slot.timeentry))
              : undefined
          if (stoppedSlot) return { ...slot, ...stoppedSlot, id: slot.id }
          if (stoppedTimeEntrySlot) {
            return { ...slot, stop: stoppedTimeEntrySlot.stop }
          }
          if (
            startedSlot &&
            !slot.stop &&
            String(slot.id) !== String(startedSlot.id)
          ) {
            return {
              ...slot,
              stop: startedSlot.start ?? new Date().toISOString(),
            }
          }
          return slot
        })

        if (!startedSlot) return patchedSlots
        const dialogTimeEntryIds = new Set(
          patchedSlots
            .map(slot => slot.timeentry_data?.id ?? slot.timeentry)
            .filter(id => id != null)
            .map(String)
        )
        const startedTimeEntryId =
          startedSlot.timeentry_data?.id ?? startedSlot.timeentry
        if (
          startedTimeEntryId == null ||
          !dialogTimeEntryIds.has(String(startedTimeEntryId))
        ) {
          return patchedSlots
        }
        const existingIndex = patchedSlots.findIndex(
          slot => String(slot.id) === String(startedSlot.id)
        )
        return existingIndex === -1
          ? [...patchedSlots, startedSlot]
          : patchedSlots.map((slot, index) =>
              index === existingIndex ? { ...slot, ...startedSlot } : slot
            )
      }

      if (!startedSlot) {
        return { slots, dialogSlots: patchDialogSlots(state.dialogSlots) }
      }

      const existingIndex = slots.findIndex(
        slot => String(slot.id) === String(startedSlot.id)
      )
      if (existingIndex === -1) {
        return {
          slots: [...slots, startedSlot],
          dialogSlots: patchDialogSlots(state.dialogSlots),
        }
      }

      return {
        dialogSlots: patchDialogSlots(state.dialogSlots),
        slots: slots.map((slot, index) =>
          index === existingIndex ? { ...slot, ...startedSlot } : slot
        ),
      }
    }),
}))
